import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { ThemeToggle } from "../components/theme-toggle";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { cn } from "../lib/utils";
import logoImg from "../assets/img/WebLogo.jpg";

const MainHeader = ({ onScheduleClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { name: "Home", path: "/" },
    {
      name: "About Us",
      path: "/aboutOethical",
      submenu: [
        { name: "Oethical Technology", path: "/aboutOethical" },
        { name: "About Director", path: "/aboutDirector" },
      ],
    },
    {
      name: "Products",
      path: "/eduAdminHub",
      submenu: [
        { name: "EduAdminHub", path: "/eduAdminHub" },
        { name: "Library Management System", path: "/lms" },
        { name: "HRM", path: "/hrm" },
      ],
    },
    { name: "Projects", path: "/project" },
    { name: "Blog", path: "/blog" },
    { name: "Team", path: "/team" },
    { name: "Career", path: "/career" },
    { name: "Contact", path: "/contactUs" },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 shadow-sm">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center group"
        >
          <img 
            src={logoImg} 
            alt="Oethical Technology" 
            className="h-12 w-auto transition-transform group-hover:scale-105" 
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-2">
          {navLinks.map((link) => {
            if (link.submenu) {
              return (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger
                    className={cn(
                      "flex items-center gap-1 h-10 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative",
                      "hover:bg-accent hover:text-accent-foreground",
                      "focus:outline-none",
                      "data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
                      (isActive(link.path) || link.submenu.some((s) => isActive(s.path))) &&
                        "text-primary"
                    )}
                  >
                    {link.name}
                    <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
                    {(isActive(link.path) || link.submenu.some((s) => isActive(s.path))) && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="start" 
                    className="w-56 mt-2 bg-popover border-border shadow-lg"
                  >
                    {link.submenu.map((item) => (
                      <DropdownMenuItem key={item.name} asChild>
                        <Link
                          to={item.path}
                          className={cn(
                            "cursor-pointer w-full",
                            isActive(item.path) && "bg-accent text-accent-foreground"
                          )}
                        >
                          {item.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }
            return (
              <Link
                key={link.name}
                to={link.path}
                className={cn(
                  "h-10 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 relative",
                  "hover:bg-accent hover:text-accent-foreground",
                  "focus:outline-none",
                  isActive(link.path) &&
                    "text-primary font-semibold"
                )}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Right Side Actions */}
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Button
            onClick={onScheduleClick}
            className="hidden md:flex"
          >
            Get Started
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Mobile Menu Panel */}
          <div className="lg:hidden fixed top-16 left-0 right-0 z-50 bg-background border-b shadow-xl animate-in slide-in-from-top-2">
            <div className="container py-6 space-y-1 max-h-[calc(100vh-5rem)] overflow-y-auto">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.submenu ? (
                    <div className="space-y-1">
                      <div className="px-4 py-3 text-sm font-semibold text-foreground border-b border-border">
                        {link.name}
                      </div>
                      <div className="pl-4 space-y-1">
                        {link.submenu.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={cn(
                              "block px-4 py-3 text-sm rounded-md transition-colors",
                              "text-muted-foreground hover:text-foreground hover:bg-accent",
                              isActive(item.path) &&
                                "text-primary bg-accent font-medium"
                            )}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "block px-4 py-3 text-sm font-medium rounded-md transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        isActive(link.path) &&
                          "text-primary bg-accent font-semibold"
                      )}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Mobile CTA */}
              <div className="pt-4 mt-4 border-t border-border">
                <Button
                  onClick={() => {
                    onScheduleClick();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full font-semibold"
                  size="lg"
                >
                  Schedule Demo
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default MainHeader;
